import React, {useEffect, useRef, useState} from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import Modal from 'react-native-modal';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useSelector} from 'react-redux';

import Icon from '/component/base/Icon';
import {RANGE_STATES} from '/component/base/RadioButton/filterRange.constants';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import VolumePerformanceTestIDs from '/constant/TestIDs/VolumePerformance';
import {useTranslation} from '/provider/TranslationProvider';
import {RootState} from '/redux/reducer';

import {
  FILTER_VALUES,
  ORDER_CATEGORIES,
  PERIODS,
} from '../../constants/volumePerformanceReport.constants';
import {createStyles} from '../../styles/VolPerfModalFilter.style';
import {VolPerfModalFilterProps} from '../../types/VolPerfComponent.types';
import {
  FilterKey,
  FilterSelections,
  RadioOptionConfig,
} from '../../types/VolPerfFilter.types';
import {VolPerfFilterRangeDetails} from '../../types/VolPerfRange.types';
import {VolumePerformanceFilters} from '../../types/volumePerformanceReport.types';
import {
  hasValidationErrors,
  validateCurrentInputs,
} from '../../util/VolPerfFilterRangeInputRadioGroup.util';

import FilterRangeInputRadioGroup from './VolPerfFilterRangeInputRadioGroup';

const DEFAULT_FILTER_SELECTIONS: FilterSelections = {
  orderType: FILTER_VALUES.ALL,
  orderStatus: FILTER_VALUES.ALL,
  accountType: FILTER_VALUES.ALL,
};

const getDefaultPriceDetails = (): VolPerfFilterRangeDetails => ({
  options: [],
  isFilterEnabled: false,
  minDigit: 0,
  maxDigit: 7,
  resultRangeState: RANGE_STATES.ABOVE as any,
  filterByText: 'Price - Subtotal Consumables',
  aboveValue: '',
  belowValue: '',
  isError: true,
  ValidationError: false,
  keyboardType: 'numbers-and-punctuation',
  isFloatAllowed: false,
  isNegativeNumAllowed: true,
});

const getDefaultQualifiedVolumeDetails = (): VolPerfFilterRangeDetails => ({
  options: [],
  isFilterEnabled: false,
  minDigit: 0,
  maxDigit: 7,
  resultRangeState: RANGE_STATES.ABOVE as any,
  filterByText: 'QV',
  aboveValue: '',
  belowValue: '',
  isError: true,
  ValidationError: false,
  keyboardType: 'numbers-and-punctuation',
  isFloatAllowed: false,
  isNegativeNumAllowed: true,
});

export const VolPerfModalFilter = ({
  isVisible,
  onClose,
  onApply,
  onClear,
}: VolPerfModalFilterProps) => {
  const {tt} = useTranslation('report.VolumePerformance');
  const insets = useSafeAreaInsets();
  const {width: screenWidth, height: screenHeight} = useWindowDimensions();
  const isLandscape = screenWidth > screenHeight;

  const modalHeight = isLandscape
    ? screenHeight
    : Platform.OS === 'android'
    ? screenHeight > 684
      ? screenHeight * 0.85
      : screenHeight * 0.9
    : screenHeight;

  const styles = createStyles({
    isLandscape,
    topInset:
      Platform.OS === 'ios'
        ? Math.max(insets.top, 16)
        : isLandscape
        ? insets.top
        : 0,
    bottomInset:
      Platform.OS === 'ios'
        ? Math.max(insets.bottom, 16)
        : isLandscape
        ? insets.bottom
        : 0,
    leftInset: isLandscape ? insets.left : 0,
    rightInset: isLandscape ? insets.right : 0,
    modalHeight,
  });

  const filters = useSelector(
    (state: RootState) => state.volumePerformanceReport.filters,
  );

  const isScheduledTab = filters?.orderCategory === ORDER_CATEGORIES.TEMPLATES;
  const isScheduledMonthSelection =
    filters?.period !== undefined &&
    [PERIODS.LAST_MONTH, PERIODS.CURRENT_MONTH, PERIODS.NEXT_MONTH].includes(
      filters.period,
    );
  const showCurrentMonthLabel = isScheduledTab && isScheduledMonthSelection;

  const [selectedFilters, setSelectedFilters] = useState<FilterSelections>(
    DEFAULT_FILTER_SELECTIONS,
  );
  const [isNewClient, setIsNewClient] = useState(false);
  const [priceDetails, setPriceDetails] = useState<VolPerfFilterRangeDetails>(
    getDefaultPriceDetails(),
  );
  const [qualifiedVolumeDetails, setQualifiedVolumeDetails] =
    useState<VolPerfFilterRangeDetails>(getDefaultQualifiedVolumeDetails());
  const [priceResetTrigger, setPriceResetTrigger] = useState(0);
  const [qualifiedVolumeResetTrigger, setQualifiedVolumeResetTrigger] =
    useState(0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  const scrollViewRef = useRef<ScrollView>(null);
  const currentY = useRef(0);

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => {
        setKeyboardVisible(true);
        setTimeout(() => {
          scrollViewRef.current?.scrollTo({
            y: currentY.current + 50,
            animated: true,
          });
        }, 300);
      },
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setKeyboardVisible(false);
      },
    );
    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const initialSelections: FilterSelections = {
      orderType: filters.orderType || FILTER_VALUES.ALL,
      orderStatus: filters.orderStatus || FILTER_VALUES.ALL,
      accountType: filters.accountType || FILTER_VALUES.ALL,
    };

    const currentStatusOptions: string[] = isScheduledTab
      ? [
          FILTER_VALUES.ALL,
          FILTER_VALUES.SCHEDULED_PREMIERE,
          FILTER_VALUES.CANCELLED_SCHEDULED_PREMIERE,
        ]
      : [
          FILTER_VALUES.ALL,
          FILTER_VALUES.ORDERS,
          FILTER_VALUES.RETURNS,
          FILTER_VALUES.CANCELLATIONS,
        ];

    if (!currentStatusOptions.includes(initialSelections.orderStatus)) {
      initialSelections.orderStatus = FILTER_VALUES.ALL;
    }

    setSelectedFilters(initialSelections);
    setIsNewClient(!!filters.isNewClient);

    const syncRangeDetails = (
      details: VolPerfFilterRangeDetails,
      filterRange?: {min?: number; max?: number},
    ): VolPerfFilterRangeDetails => {
      if (
        !filterRange ||
        (filterRange.min === undefined && filterRange.max === undefined)
      ) {
        return {
          ...details,
          isFilterEnabled: false,
          resultRangeState: RANGE_STATES.ABOVE as any,
          aboveValue: '',
          belowValue: '',
          isError: true,
          ValidationError: false,
        };
      }

      const {min, max} = filterRange;
      let resultRangeState = RANGE_STATES.ABOVE;
      let aboveValue = '';
      let belowValue = '';

      if (min !== undefined && max !== undefined) {
        resultRangeState = RANGE_STATES.BETWEEN;
        aboveValue = String(min);
        belowValue = String(max);
      } else if (min !== undefined) {
        resultRangeState = RANGE_STATES.ABOVE;
        aboveValue = String(min);
      } else if (max !== undefined) {
        resultRangeState = RANGE_STATES.BELOW;
        belowValue = String(max);
      }

      return {
        ...details,
        isFilterEnabled: true,
        resultRangeState: resultRangeState as any,
        aboveValue,
        belowValue,
        isError: false,
        ValidationError: false,
      };
    };

    setPriceDetails(prev => syncRangeDetails(prev, filters.consSubtotal));
    setQualifiedVolumeDetails(prev => syncRangeDetails(prev, filters.qv));
  }, [isVisible, filters, isScheduledTab]);

  const onScroll = (event: any) => {
    currentY.current = event.nativeEvent.contentOffset.y;
  };

  const handleSelectOption = (filterKey: FilterKey, value: string) => {
    setSelectedFilters(prevState => ({
      ...prevState,
      [filterKey]: value,
    }));
  };

  const createRadioOptions = (
    filterKey: FilterKey,
    options: any[],
    testIDPrefix: string,
  ): RadioOptionConfig[] =>
    options.map(option => ({
      label: option.label,
      value: option.value,
      selected: selectedFilters[filterKey] === option.value,
      testID: `${testIDPrefix}-${option.value}`,
    }));

  const handleClearPress = () => {
    setSelectedFilters(DEFAULT_FILTER_SELECTIONS);
    setIsNewClient(false);
    setPriceDetails(getDefaultPriceDetails());
    setQualifiedVolumeDetails(getDefaultQualifiedVolumeDetails());
    setPriceResetTrigger(prevState => prevState + 1);
    setQualifiedVolumeResetTrigger(prevState => prevState + 1);
    onClear();
    onClose();
  };

  const handleApplyPress = () => {
    const rangeFilters = [
      {details: priceDetails, setter: setPriceDetails},
      {details: qualifiedVolumeDetails, setter: setQualifiedVolumeDetails},
    ];

    let hasAnyError = false;

    rangeFilters.forEach(({details, setter}) => {
      if (!details.isFilterEnabled) {
        setter(prev => ({...prev, ValidationError: false}));
        return;
      }

      const errors = validateCurrentInputs({
        isFilterEnabled: details.isFilterEnabled,
        selectedRangeId: details.resultRangeState as any,
        aboveValue: details.aboveValue,
        belowValue: details.belowValue,
        minDigit: details.minDigit,
        maxDigit: details.maxDigit,
        isFloatAllowed: details.isFloatAllowed,
        isNegativeNumAllowed: details.isNegativeNumAllowed,
      });

      const hasError = hasValidationErrors(errors);
      if (hasError) {
        hasAnyError = true;
      }
      setter(prev => ({...prev, ValidationError: hasError}));
    });

    if (hasAnyError) {
      return;
    }

    const formatRange = (details: VolPerfFilterRangeDetails) => {
      if (!details.isFilterEnabled) {
        return undefined;
      }
      const min =
        details.resultRangeState === RANGE_STATES.BELOW
          ? undefined
          : Number(details.aboveValue);
      const max =
        details.resultRangeState === RANGE_STATES.ABOVE
          ? undefined
          : Number(details.belowValue);
      return {min, max};
    };

    const filtersToApply: Partial<VolumePerformanceFilters> = {
      orderType:
        selectedFilters.orderType === FILTER_VALUES.ALL
          ? undefined
          : (selectedFilters.orderType as any),
      orderStatus:
        selectedFilters.orderStatus === FILTER_VALUES.ALL
          ? undefined
          : (selectedFilters.orderStatus as any),
      accountType: selectedFilters.accountType as any,
      isNewClient,
      consSubtotal: formatRange(priceDetails) as any,
      qv: formatRange(qualifiedVolumeDetails) as any,
    };

    onApply(filtersToApply);
  };

  const orderTypeOptions = createRadioOptions(
    'orderType',
    [
      {label: tt('orderType.all', 'All'), value: FILTER_VALUES.ALL},
      {
        label: tt('orderType.premierPlus', 'Premier+'),
        value: FILTER_VALUES.PREMIER_PLUS,
      },
      {
        label: tt('orderType.onDemand', 'On-Demand'),
        value: FILTER_VALUES.ON_DEMAND,
      },
    ],
    'order-type',
  );

  const orderStatusOptions = createRadioOptions(
    'orderStatus',
    [
      {label: tt('orderStatus.all', 'All'), value: FILTER_VALUES.ALL},
      {label: tt('orderStatus.orders', 'Orders'), value: FILTER_VALUES.ORDERS},
      {
        label: tt('orderStatus.returns', 'Returns'),
        value: FILTER_VALUES.RETURNS,
      },
      {
        label: tt('orderStatus.cancellations', 'Cancellations'),
        value: FILTER_VALUES.CANCELLATIONS,
      },
    ],
    'order-status',
  );

  const nextMonthOrderStatusOptions = createRadioOptions(
    'orderStatus',
    [
      {label: tt('orderStatus.all', 'All'), value: FILTER_VALUES.ALL},
      {
        label: tt('orderStatus.scheduledPremiere', 'Scheduled Premier+'),
        value: FILTER_VALUES.SCHEDULED_PREMIERE,
      },
      {
        label: tt(
          'orderStatus.cancelledScheduledPremiere',
          'Cancelled Scheduled Premier+',
        ),
        value: FILTER_VALUES.CANCELLED_SCHEDULED_PREMIERE,
      },
    ],
    'order-status',
  );

  const accountTypeOptions = createRadioOptions(
    'accountType',
    [
      {label: tt('accountType.all', 'All'), value: FILTER_VALUES.ALL},
      {label: tt('accountType.client', 'Client'), value: FILTER_VALUES.CLIENT},
      {label: tt('accountType.coach', 'Coach'), value: FILTER_VALUES.COACH},
    ],
    'account-type',
  );

  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      style={styles.modal}
      animationIn={Platform.OS === 'web' ? 'slideInRight' : 'slideInUp'}
      animationOut={Platform.OS === 'web' ? 'slideOutRight' : 'slideOutDown'}
      useNativeDriver={false}
      hideModalContentWhileAnimating={false}
      avoidKeyboard={false}
      deviceHeight={screenHeight}
      deviceWidth={screenWidth}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 10 : 0}
        style={styles.keyboardAvoidingView}>
        <View style={styles.sheet}>
          <View style={styles.innerContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity
                onPress={onClose}
                testID={VolumePerformanceTestIDs.volPerfCloseFilters}>
                <Icon name="close" style={styles.closeIcon} />
              </TouchableOpacity>
              <Text
                accessible
                accessibilityLabel="Filters"
                style={styles.headerText}
                isBold
                testID={VolumePerformanceTestIDs.volPerfFilters}>
                {tt('filters', 'Filters')}
              </Text>
            </View>

            <ScrollView
              ref={scrollViewRef}
              style={styles.scrollView}
              onScroll={onScroll}
              scrollEventThrottle={16}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              contentContainerStyle={
                keyboardVisible
                  ? styles.scrollContentKeyboard
                  : styles.scrollContent
              }>
              <View style={styles.radioButtonsContainer}>
                {!isScheduledTab && (
                  <RadioButtonGroup
                    title={tt('orderType.title', 'Order Type')}
                    options={orderTypeOptions.map(option => ({
                      text: option.label,
                      selected: !!option.selected,
                      onPress: () =>
                        handleSelectOption('orderType', option.value),
                      testID: option.testID,
                    }))}
                    isBold
                    testID={VolumePerformanceTestIDs.volPerfFilterOrderType}
                  />
                )}

                <RadioButtonGroup
                  title={tt('orderStatus.title', 'Order Status')}
                  options={(isScheduledTab
                    ? nextMonthOrderStatusOptions
                    : orderStatusOptions
                  ).map(option => ({
                    text: option.label,
                    selected: !!option.selected,
                    onPress: () =>
                      handleSelectOption('orderStatus', option.value),
                    testID: option.testID,
                  }))}
                  isBold
                  testID={VolumePerformanceTestIDs.volPerfFilterOrderStatus}
                />

                <RadioButtonGroup
                  title={tt('accountType.title', 'Account Type')}
                  options={accountTypeOptions.map(option => ({
                    text: option.label,
                    selected: !!option.selected,
                    onPress: () =>
                      handleSelectOption('accountType', option.value),
                    testID: option.testID,
                  }))}
                  isBold
                  testID={VolumePerformanceTestIDs.volPerfFilterAccountType}
                />

                <View style={styles.filterSection}>
                  <Text
                    style={styles.sectionTitle}
                    isBold
                    accessible
                    accessibilityLabel="New Clients"
                    testID="new-clients-title">
                    {tt('newClients.title', 'New Clients')}
                  </Text>
                  <TouchableOpacity
                    style={styles.checkboxItem}
                    testID="checkbox-new-clients"
                    onPress={() => setIsNewClient(prev => !prev)}>
                    <Icon
                      name={
                        isNewClient
                          ? 'checkbox-selected'
                          : 'checkbox-unselected'
                      }
                      style={
                        isNewClient
                          ? styles.checkboxIconSelected
                          : styles.checkboxIcon
                      }
                    />
                    <Text
                      style={styles.checkboxLabel}
                      isBold
                      testID="show-new-clients">
                      {showCurrentMonthLabel
                        ? tt('current_month', 'Current Month')
                        : tt(
                            'newClients.onlyNewClients',
                            'Show only new clients orders',
                          )}
                    </Text>
                  </TouchableOpacity>
                </View>

                {isScheduledTab && (
                  <FilterRangeInputRadioGroup
                    title={tt(
                      'priceSubtotalConsumables.title',
                      'Price - Subtotal Consumables',
                    )}
                    filterRangeDetails={priceDetails}
                    setFilterRangeDetails={setPriceDetails}
                    resetTrigger={priceResetTrigger}
                  />
                )}

                <FilterRangeInputRadioGroup
                  title={tt('qualifiedVolume.title', 'Qualified Volume (QV)')}
                  filterRangeDetails={qualifiedVolumeDetails}
                  setFilterRangeDetails={setQualifiedVolumeDetails}
                  resetTrigger={qualifiedVolumeResetTrigger}
                />
              </View>
            </ScrollView>

            <View style={styles.fixedButtonContainer}>
              <TouchableOpacity
                testID={VolumePerformanceTestIDs.volPerfClearAllButton}
                onPress={handleClearPress}
                style={styles.clearButton}>
                <Text style={styles.clearText} isBold>
                  {tt('clearAll', 'Clear All')}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                testID={VolumePerformanceTestIDs.volPerfApplyFiltersButton}
                onPress={handleApplyPress}
                style={styles.applyButton}>
                <Text style={styles.applyText} isBold>
                  {tt('applyFilters', 'Apply Filters')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default VolPerfModalFilter;
